/**
 * pressure-policy.ts — main-thread power & pressure signal aggregator.
 *
 * Aggregates four signals into a single `PressureLevel` that the rest of
 * the player consumes for adaptive throttling:
 *
 *   1. Compute Pressure API (PressureObserver — Chrome 115+)
 *      The primary signal. Reflects real CPU/thermal stress as observed
 *      by the OS. Levels: 'nominal' / 'fair' / 'serious' / 'critical'.
 *
 *   2. Battery Status API (navigator.getBattery — Chromium only)
 *      Augments: lifts level to at least 'fair' when on battery + level
 *      ≤ 0.5. Optional — graceful fallback when API absent.
 *
 *   3. Page Visibility API (document.visibilityState)
 *      When the tab is hidden, the browser already throttles rAF, but we
 *      can additionally relax worker-tick cadence.
 *
 *   4. CSS media-queries (prefers-reduced-motion, prefers-reduced-data)
 *      User-explicit signals that we should drop down to a low-power UX.
 *
 * The module is deliberately Chromium-friendly without being
 * Chromium-required. On Firefox/Safari (no PressureObserver, no Battery
 * API) the level stays at 'nominal' and the player runs the status-quo
 * (full quality, full cadence). That matches reality: those browsers
 * also don't expose the signals needed for safe adaptation.
 */

// ── Type shims (these APIs aren't in lib.dom yet) ─────────────────────

type PressureLevel = 'nominal' | 'fair' | 'serious' | 'critical';

interface PressureRecord {
  source: 'cpu' | 'gpu' | 'thermals' | 'power';
  state:  PressureLevel;
  time:   number;
}

interface PressureObserverInit {
  sampleInterval?: number;
}

interface PressureObserverImpl {
  observe(source: 'cpu' | 'gpu' | 'thermals' | 'power'): Promise<void>;
  disconnect(): void;
}

interface PressureObserverCtor {
  new (
    cb: (records: PressureRecord[]) => void,
    init?: PressureObserverInit,
  ): PressureObserverImpl;
  knownSources?: readonly ('cpu' | 'gpu' | 'thermals' | 'power')[];
}

declare const PressureObserver: PressureObserverCtor | undefined;

interface BatteryManager extends EventTarget {
  charging:    boolean;
  level:       number;  // 0..1
  // chargingTime / dischargingTime omitted — unused.
}

interface NavigatorBattery {
  getBattery?: () => Promise<BatteryManager>;
}

// ── Public types ──────────────────────────────────────────────────────

export type { PressureLevel };

export interface PressureSnapshot {
  /** Aggregate level: max of CPU pressure + battery-derived level. */
  level: PressureLevel;
  /** Raw CPU pressure from Compute Pressure API, or null if unavailable. */
  cpuPressure: PressureLevel | null;
  /** True when on battery AND not charging, or null if Battery API absent. */
  onBattery: boolean | null;
  /** Battery level 0..1, or null if API absent. */
  batteryLevel: number | null;
  /** True when document.visibilityState === 'hidden'. */
  hidden: boolean;
  /** matchMedia: user prefers reduced motion. */
  reducedMotion: boolean;
  /** matchMedia: user prefers reduced data. */
  reducedData: boolean;
}

export type PressureChangeListener = (snap: PressureSnapshot) => void;

// ── Implementation ────────────────────────────────────────────────────

const LEVEL_RANK: Record<PressureLevel, number> = {
  nominal:  0,
  fair:     1,
  serious:  2,
  critical: 3,
};

const RANK_TO_LEVEL: PressureLevel[] = ['nominal', 'fair', 'serious', 'critical'];

function maxLevel(...levels: (PressureLevel | null | undefined)[]): PressureLevel {
  let best = 0;
  for (const l of levels) {
    if (l && LEVEL_RANK[l] > best) best = LEVEL_RANK[l];
  }
  return RANK_TO_LEVEL[best];
}

export class PressurePolicy extends EventTarget {
  private cpuPressure: PressureLevel | null = null;
  private onBattery: boolean | null = null;
  private batteryLevel: number | null = null;
  private hidden = false;
  private reducedMotion = false;
  private reducedData = false;

  private observer: PressureObserverImpl | null = null;
  private battery: BatteryManager | null = null;
  private mqlMotion: MediaQueryList | null = null;
  private mqlData:   MediaQueryList | null = null;

  private lastEmittedLevel: PressureLevel = 'nominal';
  // Hysteresis: require sustained transition to avoid event-storms when
  // a signal hovers around a threshold.
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly DEBOUNCE_MS = 300;

  /**
   * Create + initialize a policy. Always succeeds — missing APIs are
   * silently tolerated and reflected as null fields in the snapshot.
   */
  static async create(): Promise<PressurePolicy> {
    const p = new PressurePolicy();
    await p.init();
    return p;
  }

  get snapshot(): PressureSnapshot {
    return {
      level:        this.computeAggregate(),
      cpuPressure:  this.cpuPressure,
      onBattery:    this.onBattery,
      batteryLevel: this.batteryLevel,
      hidden:       this.hidden,
      reducedMotion: this.reducedMotion,
      reducedData:   this.reducedData,
    };
  }

  on(listener: PressureChangeListener): () => void {
    const handler = () => listener(this.snapshot);
    this.addEventListener('change', handler);
    return () => this.removeEventListener('change', handler);
  }

  destroy(): void {
    if (this.observer) {
      try { this.observer.disconnect(); } catch { /* noop */ }
      this.observer = null;
    }
    if (this.battery) {
      this.battery.removeEventListener('chargingchange', this.onBatteryChange);
      this.battery.removeEventListener('levelchange',    this.onBatteryChange);
      this.battery = null;
    }
    if (this.mqlMotion) {
      this.mqlMotion.removeEventListener('change', this.onMotionChange);
      this.mqlMotion = null;
    }
    if (this.mqlData) {
      this.mqlData.removeEventListener('change', this.onDataChange);
      this.mqlData = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  // ── Initialization ────────────────────────────────────────────────

  private async init(): Promise<void> {
    // Compute Pressure API.
    if (typeof PressureObserver !== 'undefined') {
      try {
        this.observer = new PressureObserver(
          (records) => this.onPressureRecords(records),
          { sampleInterval: 1000 },
        );
        await this.observer.observe('cpu');
      } catch {
        this.observer = null;
      }
    }

    // Battery Status API.
    const nav = navigator as unknown as NavigatorBattery;
    if (typeof nav.getBattery === 'function') {
      try {
        this.battery = await nav.getBattery();
        this.onBattery    = !this.battery.charging;
        this.batteryLevel = this.battery.level;
        this.battery.addEventListener('chargingchange', this.onBatteryChange);
        this.battery.addEventListener('levelchange',    this.onBatteryChange);
      } catch {
        this.battery = null;
      }
    }

    // Visibility.
    if (typeof document !== 'undefined') {
      this.hidden = document.visibilityState === 'hidden';
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    // Media queries.
    if (typeof matchMedia === 'function') {
      try {
        this.mqlMotion = matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion = this.mqlMotion.matches;
        this.mqlMotion.addEventListener('change', this.onMotionChange);
      } catch { this.mqlMotion = null; }
      try {
        this.mqlData = matchMedia('(prefers-reduced-data: reduce)');
        this.reducedData = this.mqlData.matches;
        this.mqlData.addEventListener('change', this.onDataChange);
      } catch { this.mqlData = null; }
    }

    this.lastEmittedLevel = this.computeAggregate();
  }

  // ── Change handlers (arrow-bound for removeEventListener parity) ──

  private onPressureRecords = (records: PressureRecord[]): void => {
    if (records.length === 0) return;
    // Use the most recent record; PressureObserver buffers can deliver
    // several at once when the page was suspended.
    const latest = records[records.length - 1];
    if (latest.source !== 'cpu') return;
    if (this.cpuPressure === latest.state) return;
    this.cpuPressure = latest.state;
    this.scheduleEmit();
  };

  private onBatteryChange = (): void => {
    if (!this.battery) return;
    this.onBattery    = !this.battery.charging;
    this.batteryLevel = this.battery.level;
    this.scheduleEmit();
  };

  private onVisibilityChange = (): void => {
    const next = typeof document !== 'undefined'
      ? document.visibilityState === 'hidden'
      : false;
    if (next === this.hidden) return;
    this.hidden = next;
    this.scheduleEmit();
  };

  private onMotionChange = (e: MediaQueryListEvent): void => {
    if (e.matches === this.reducedMotion) return;
    this.reducedMotion = e.matches;
    this.scheduleEmit();
  };

  private onDataChange = (e: MediaQueryListEvent): void => {
    if (e.matches === this.reducedData) return;
    this.reducedData = e.matches;
    this.scheduleEmit();
  };

  // ── Aggregation + emission ────────────────────────────────────────

  private computeAggregate(): PressureLevel {
    // Battery → at-least-fair when on battery + low charge.
    let battLevel: PressureLevel = 'nominal';
    if (this.onBattery === true && this.batteryLevel !== null) {
      if (this.batteryLevel <= 0.20)      battLevel = 'serious';
      else if (this.batteryLevel <= 0.50) battLevel = 'fair';
    }

    // Reduced-motion / reduced-data → at-least-fair.
    const userPref: PressureLevel =
      (this.reducedMotion || this.reducedData) ? 'fair' : 'nominal';

    // Hidden → at-least-fair (rAF is throttled but JS work still costs).
    const visibility: PressureLevel = this.hidden ? 'fair' : 'nominal';

    return maxLevel(this.cpuPressure ?? 'nominal', battLevel, userPref, visibility);
  }

  private scheduleEmit(): void {
    if (this.debounceTimer !== null) return;
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      const next = this.computeAggregate();
      if (next === this.lastEmittedLevel) return;
      this.lastEmittedLevel = next;
      this.dispatchEvent(new Event('change'));
    }, PressurePolicy.DEBOUNCE_MS);
  }
}
